/* eslint-disable react-hooks/rules-of-hooks */
import React from 'react';
import PropTypes from 'prop-types';
import useStyle from '@front10/helpers/dist/UseStyles';

//-------------------------------------
// Basic components here
//-------------------------------------
import Header from '../../elements/Header';
import DatesRangeSelector from '../../controls/DatesRangeSelector';
import Row from '../../elements/Row';
import Col from '../../elements/Col';
import Box from '../../elements/Box';
import AirBnBMoreFilters from '../../controls/AirBnBMoreFilters';
import LocationSelector from '../../controls/LocationSelector';
import Disclaimer from '../../others/Disclaimer';
import Skeleton from '../../others/Skeleton/SkeletonFaresCarouselBackgroundImage';
import Loading from '../../others/Loading';
import NoFeaturedPopularFaresAlert from '../../alerts/NoFeaturedPopularFaresAlert';
import ContainerErrorAlert from '../../alerts/ContainerErrorAlert';
import Button from '../../elements/Button';
import Icon from '../../elements/Icon';
import Text from '../../elements/Text';
import Carousel from '../../controls/ElasticCarousel';
import AirBnBIcon from '../../elements/AirBnbIcon';
import OfferVerticalCard from '../../items/OfferVerticalCard';
import GuestSelector from '../../controls/GuestsSelector';
import { GuestsSelectorContainer } from '../../controls/GuestsSelector/commons';

// airbnb actions
import detailHouse from '../../../_integrations/airbnb/actions';

//-------------------------------------
// Every mundo integration
//-------------------------------------
import { getOffers } from '../../../_integrations/airbnb/listings';
import { getAirports as getCities } from '../../../_integrations/everymundo/airports';
import {
  hookFaresOnFilterChange,
  hookComponentDidMount,
  hookOnErrorAlert
} from '../_commons/featuredOffersHook';

//-------------------------------------
// Integration Layer Default Actions
//-------------------------------------
import useClickListing from '../../../_integrations/everymundo/tracking/_experimental/events/useClickListing';
import useChangeBudget from '../../../_integrations/everymundo/tracking/_experimental/events/useChangeBudget';
import useChangeHomeType from '../../../_integrations/everymundo/tracking/_experimental/events/useChangeHomeType';
import useChangeGuests from '../../../_integrations/everymundo/tracking/_experimental/events/useChangeGuests';
import useChangeDateRange from '../../../_integrations/everymundo/tracking/_experimental/events/useChangeDateRange';
import useChangeOrigin from '../../../_integrations/everymundo/tracking/_experimental/events/useChangeOrigin';

import withDeepMergeDefaultProps from '../../utils/WithDeepMergeDefaultProps';

import { computeDateRange, isSameDateRange } from '../../../helpers/date';
import { isEmpty } from '../../../helpers/utils';

const cardType = 'listing';

class AirBnBCarouselContainer extends React.Component {
  state = {
    errorLoading: false,
    isLoadingDepartureAirports: false,
    isLoadingOffers: false,
    departureAirports: this.props.initialDepartureAirports || [],
    offers: this.props.initialoffertsCard || [],
    isSkeletonShown: this.props.showSkeletonScreen,
    origin: this.props.prePopulation.origin,
    dateRange: this.props.prePopulation.dateRange
      ? computeDateRange(this.props.prePopulation.dateRange)
      : {},
    budget:
      this.props.prePopulation &&
      this.props.prePopulation.budget &&
      this.props.prePopulation.budget.maximum
        ? this.props.prePopulation.budget.maximum
        : null,
    homeType:
      this.props.prePopulation && this.props.prePopulation.homeType
        ? this.props.prePopulation.homeType
        : [],
    guests:
      this.props.prePopulation.guests && !isEmpty(this.props.prePopulation.guests)
        ? this.props.prePopulation.guests.adults + this.props.prePopulation.guests.children
        : 0
  };

  static getCarouselSettings(props, state) {
    const { dateRange } = state;
    const {
      components: { carouselSettings }
    } = props;
    carouselSettings.itemSettings = Object.assign(
      {
        onClick: ({ data }) => {
          props.fareItemOnClickAction(props, data, dateRange);
        },
        callToActionOnClick: ({ data }) => {
          props.fareItemOnClickAction(props, data, dateRange);
        }
      },
      carouselSettings.itemSettings
    );

    return carouselSettings;
  }

  carouselSettings = AirBnBCarouselContainer.getCarouselSettings(this.props, this.state);

  header = (
    <Header
      floatLeft
      content={this.props.translate(this.props.components.titleKey, 'Trending destination')}
      {...this.props.components.titleSettings}
    />
  );

  skeleton = (<Skeleton {...this.props.skeletonSettings} position="absolute" width="100" />);

  disclaimer = (
    <Disclaimer
      labelKey="cmp-fares-carousel-background-image-container-disclaimer"
      {...this.props.components.disclaimer}
    />
  );

  constructor(props) {
    super(props);
    this.dispatchGuests = React.createRef();
  }

  componentDidMount = () => {
    const { initialoffertsCard, containerType } = this.props;
    if (containerType !== 'dumb') {
      // Preparing for the real hooks :)
      // we are using the "hook" concept here to reuse logic
      // between similar containers in case we need to
      if (initialoffertsCard) {
        this.setState({
          isSkeletonShown: false
        });
      } else {
        hookComponentDidMount(this);
      }
    } else {
      hookComponentDidMount(this);
    }
  };

  renderAirport = (item) => {
    const { showCityCode } = this.props;
    const code = item.label ? item.label.substring(0, 3).toUpperCase() : '';
    return (
      <>
        {showCityCode && (
          <Box
            border
            paddingWidth="1"
            marginRight="2"
            floatLeft
            textAlign="center"
            fontWeight="bold"
            style={{ minWidth: '45px' }}
          >
            <span style={{ paddingTop: '2px' }}>{code}</span>
          </Box>
        )}
        <Box>
          <span>{item.label}</span>
        </Box>
      </>
    );
  };

  handleAirport = (origin) => {
    // Notifications via props (if Everymundo wants
    // to pass notifications they can do it via props)
    this.props.onChangeDepartureCity(this.props, origin.value, cardType);

    this.setState({ origin: origin.value }, () => {
      hookFaresOnFilterChange(this);
    });
  };

  clearAirport = () => {
    this.setState({ origin: '' }, () => {
      hookFaresOnFilterChange(this);
    });
  };

  tryAgain = () => hookOnErrorAlert(this);

  onChangeDate = ({ start, end }) => {
    if (!isSameDateRange({ start, end }, this.state.dateRange)) {
      // Notifications via props (if Everymundo wants
      // to pass notifications they can do it via props)
      this.props.onChangeDate(this.props, start, end, cardType);

      this.setState({ dateRange: { start, end } }, () => {
        hookFaresOnFilterChange(this);
      });
    }
  };

  onClearDate = () => {
    this.setState({ dateRange: {} }, () => {
      hookFaresOnFilterChange(this);
    });
  };

  onDateInputChange = (value, name) => {
    this.setState({
      [`${name}InputValue`]: value
    });
  };

  onChangeMoreFilter = ({ budget, homeType }) => {
    // Notifications via props (if Everymundo wants
    // to pass notifications they can do it via props)
    if (this.state.budget !== budget) {
      this.props.onChangeBudget(this.props, budget, cardType);
    }
    if (this.state.homeType !== homeType) {
      this.props.onChangeHomeType(this.props, homeType);
    }
    this.setState({ budget, homeType }, () => {
      hookFaresOnFilterChange(this);
    });
  };

  onClearMoreFilter = () => {
    this.setState({ budget: null, homeType: null }, () => {
      hookFaresOnFilterChange(this);
    });
  };

  onChangeGuest = ({ adults, children }) => {
    const { guests } = this.state;
    const { onChangeGuests } = this.props;
    const newGuests = adults + children;
    if (newGuests !== guests) {
      onChangeGuests(this.props, newGuests);
      this.setState({ guests: newGuests }, () => {
        hookFaresOnFilterChange(this);
      });
    }
  };

  resetFilters = () => {
    this.setState(
      { origin: '', budget: null, dateRange: { start: undefined, end: undefined } },
      () => {
        this.datesRangeSelector.reset();
        this.moreFilters.reset();
        this.dispatchGuests.current({ type: 'CLEAR' });
        hookFaresOnFilterChange(this);
      }
    );
  };

  render() {
    const {
      departureAirports,
      offers,
      isSkeletonShown,
      origin,
      isLoadingOffers,
      errorLoading,
      isLoadingDepartureAirports,
      dateRange,
      budget,
      homeType,
      guests
    } = this.state;
    const {
      components,
      translate,
      showSkeletonScreen,
      showLoading,
      direction,
      showTitle,
      showFilters,
      showDisclaimer,
      prePopulation,
      airBnBIcon,
      theme
    } = this.props;
    const [className, style] = useStyle(this.props, { direction, theme });
    const myStyle = Object.assign({ minHeight: '600px' }, style);
    const isFiltered =
      origin !== '' ||
      (dateRange.start && dateRange.end) ||
      (budget && budget !== '') ||
      (homeType && homeType.length > 0) ||
      (guests !== null && guests !== undefined && guests > 0);
    const defaultGuests =
      prePopulation.guests &&
      typeof prePopulation.guests === 'object' &&
      Object.keys(prePopulation.guests).length > 0
        ? prePopulation.guests
        : { adults: 0, children: 0, infants: 0 };

    const renderContent = (
      <Box
        position="relative"
        className={`${className} ${AirBnBCarouselContainer.componentName} `}
        style={myStyle}
        dataTestId="carousel"
      >
        {isLoadingOffers && showLoading && <Loading {...components.loading} />}
        {showSkeletonScreen && isSkeletonShown && this.skeleton}
        {showTitle && (
          <Row {...components.headerContentSettings} gutters>
            <Col display="inline-flex" alignItems="center">
              <AirBnBIcon {...airBnBIcon} />
              <Text paddingWidth="1">{this.header}</Text>
            </Col>
          </Row>
        )}
        {showFilters && (
          <Row {...components.filterContentSettings} gutters>
            <Col lg="12" textAlign="right">
              <Button
                paddingWidth="0"
                textTransform="uppercase"
                fontSize="3"
                {...components.clearAll}
                onClick={this.resetFilters}
                disabled={!isFiltered}
                dataTestId="clearAllBtn"
                display="inline-flex"
                alignItems="center"
              >
                {translate('cmp-filter-clear', 'Clear all')}
                <Icon icon="close" paddingLeft="1" size={18} {...components.clearAll.icon} />
              </Button>
            </Col>
            <Col lg="3" paddingTop="2" className="fr-pt-lg-0">
              <LocationSelector
                {...components.locationSelector}
                onChange={this.handleAirport}
                options={departureAirports}
                showCode={false}
                placeholder={translate('cmp-location-destination-placeholder', 'Destination City')}
                itemRenderer={this.renderAirport}
                defaultValue={origin}
                onClear={this.clearAirport}
                loading={isLoadingDepartureAirports}
              />
            </Col>
            <Col paddingTop="2" className="fr-pt-lg-0">
              <DatesRangeSelector
                {...components.datesRangeSelector}
                defaultValue={dateRange}
                departInputPlaceholderLabelKey="cmp-airbnb-date-date-range-from-placeholder"
                returnInputPlaceholderLabelKey="cmp-airbnb-date-date-range-to-placeholder"
                onChange={this.onChangeDate}
                onClear={this.onClearDate}
                onInputChange={this.onDateInputChange}
                focusReferences={(ref) => {
                  this.datesRangeSelector = ref;
                }}
              />
            </Col>
            <Col lg="2" paddingTop="2" className="fr-pt-lg-0">
              <GuestsSelectorContainer initialValue={defaultGuests}>
                {([newGuests, dispatch]) => {
                  this.dispatchGuests.current = dispatch; // Escape hatch to sort the scope limitation of the render prop
                  return (
                    <GuestSelector
                      {...components.guestsSelector}
                      value={newGuests}
                      onApply={this.onChangeGuest}
                      dispatch={dispatch}
                      focusReferences={(ref) => {
                        this.guest = ref;
                      }}
                    />
                  );
                }}
              </GuestsSelectorContainer>
            </Col>
            <Col lg="3" paddingTop="2" className="fr-pt-lg-0">
              <AirBnBMoreFilters
                {...components.moreFilter}
                onChange={this.onChangeMoreFilter}
                defaultBudget={budget}
                defaultHomeType={homeType}
                focusReferences={(ref) => {
                  this.moreFilters = ref;
                }}
                onClear={this.onClearMoreFilter}
              />
            </Col>
          </Row>
        )}
        <Row {...components.mainContentSettings} gutters>
          {errorLoading && (
            <Col>
              <ContainerErrorAlert
                tryAgain={this.tryAgain}
                labelKeySubTitleIntro="cmp-airbnb-error-reload-intro"
                labelKeySubTitle="cmp-text-tryagain"
                {...components.alert}
              />
            </Col>
          )}
          {offers.length === 0 && !errorLoading && (
            <Col>
              <NoFeaturedPopularFaresAlert
                labelKeyTitle="cmp-alert-no-fares-available"
                labelKeyLink="cmp-airbnb-alert-link-more-homes"
                showLink
                onLinkClick={detailHouse}
                {...components.noResultsPlaceHolder}
              />
            </Col>
          )}
          {offers.length !== 0 && !errorLoading && (
            <Carousel
              {...this.carouselSettings}
              ItemComponentInstance={OfferVerticalCard}
              itemsData={offers}
            />
          )}
        </Row>
        {showDisclaimer && (
          <Row {...components.disclaimerContentSettings}>
            <Col>{this.disclaimer}</Col>
          </Row>
        )}
      </Box>
    );

    return renderContent;
  }
}

AirBnBCarouselContainer.propTypes = {
  /**
   * To be used to set if the container is a controlled one
   */
  containerType: PropTypes.string,
  /**
   * ClassName of the components
   */
  className: PropTypes.string,
  /**
   * Direction of the component.
   */
  direction: PropTypes.oneOf(['rtl', 'ltr']),
  /**
   * Props to fare list
   */
  fareList: PropTypes.objectOf(PropTypes.any),
  /*
   * Action to be executed when clicking in fareItem
   */
  fareItemOnClickAction: PropTypes.func,
  /**
   * Function for request to obtain airports
   */
  getCities: PropTypes.func,
  /**
   * Function to getFares
   */
  getOffers: PropTypes.func,
  /**
   * Used to prepopulate the component
   */
  initialDepartureAirports: PropTypes.arrayOf(PropTypes.any),
  /**
   * Used to prepopulate the component
   */
  initialoffertsCard: PropTypes.arrayOf(PropTypes.any),
  /**
   * Loading Component
   */
  loading: PropTypes.objectOf(PropTypes.any),
  /**
   * Component Behavior when there are not results available
   */
  noFaresBehavior: PropTypes.oneOf(['hidden', 'showNoResults']),
  /**
   * Handle the action when change departure city
   */
  onChangeDepartureCity: PropTypes.func,
  /**
   * Handle the action when change date
   */
  onChangeDate: PropTypes.func,
  /**
   * Handle the action when change budget price
   */
  onChangeBudget: PropTypes.func,
  /**
   * Handle the action when change home type
   */
  onChangeHomeType: PropTypes.func,
  /**
   * Handle the action when change guest
   */
  onChangeGuests: PropTypes.func,
  /**
   * Props to other fixed title
   */
  otherPopularDestinationsLabel: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to other variable title
   */
  otherPopularDestinationsValue: PropTypes.objectOf(PropTypes.any),
  /**
   * Component PrePopulation
   */
  prePopulation: PropTypes.objectOf(PropTypes.any),
  /**
   * Show Airport Iata Code in locations
   */
  showCityCode: PropTypes.bool,
  /**
   * Show disclaimer
   */
  showDisclaimer: PropTypes.bool,
  /**
   * Show filters
   */
  showFilters: PropTypes.bool,
  /**
   * Determine if the component will show the skeleton screen while
   * loading (Default value will be false)
   */
  showSkeletonScreen: PropTypes.bool,
  /**
   * Shows Loading Component
   */
  showLoading: PropTypes.bool,
  /**
   * Show title
   */
  showTitle: PropTypes.bool,
  /**
   * SkeletonSettings Component
   */
  skeletonSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Css style applied to the component
   */
  style: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to translate
   */
  translate: PropTypes.func,
  /**
   * Sub-Components settings
   */
  components: PropTypes.objectOf(PropTypes.any),
  /**
   * airBnBIcon settings
   */
  airBnBIcon: PropTypes.objectOf(PropTypes.any),
  /**
   * Possible interactions will be Popup or Direct Deeplink
   */
  fareItemInteractionType: PropTypes.oneOf(['popup', 'deeplink']),
  /**
   * LocalizationData via moment to be used as a date translator
   * https://github.com/moment/moment/blob/develop/src/locale/es.js
   */
  localizationData: PropTypes.objectOf(PropTypes.any),
  /**
   * Theme object on the component
   */
  theme: PropTypes.objectOf(PropTypes.any),
  /**
   * Parameters tracking
   */
  tracking: PropTypes.objectOf(PropTypes.any)
};

AirBnBCarouselContainer.defaultProps = {
  components: {
    moreFilter: {
      shadow: '@theme:input.shadow',
      rounded: '@theme:input.rounded'
    },
    alert: {
      bgColor: 'light',
      textAlign: 'center',
      padding: '3',
      rounded: '@theme:card.rounded'
    },
    budgetSetting: {},
    carouselSettings: {},
    clearAll: {
      color: 'link',
      bgColor: 'transparent',
      textColor: 'light-18',
      borderNone: true,
      icon: {
        iconColor: 'light-18'
      }
    },
    datesRangeSelector: {
      shadow: '@theme:input.shadow',
      rounded: '@theme:input.rounded'
    },
    disclaimer: {
      marginTop: '2'
    },
    disclaimerContentSettings: {
      marginTop: '2'
    },
    errorAlert: {
      labelKeyTitle: 'cmp-container-error-message',
      labelKeySubTitle: 'cmp-container-error-reload-deals'
    },
    noResultsPlaceHolder: {
      bgColor: 'light',
      rounded: '@theme:card.rounded'
    },
    mainContentSettings: {
      marginTop: '3'
    },
    headerContentSettings: {},
    filterContentSettings: {},
    title: {},
    titleKey: 'cmp-fares-carousel-background-image-container-title',
    titleSettings: {
      margin: '0',
      textAlign: 'left',
      fontSize: '6'
    },
    guestsSelector: {
      shadow: '@theme:input.shadow',
      rounded: '@theme:input.rounded'
    },
    locationSelector: {}
  },
  containerType: 'stateful',
  className: '',
  direction: 'ltr',
  fareList: {},
  fareItemOnClickAction: useClickListing,
  getCities,
  getOffers,
  initialDepartureAirports: null,
  initialoffertsCard: null,
  loading: {},
  noFaresBehavior: 'hidden',
  onChangeDepartureCity: useChangeOrigin,
  onChangeDate: useChangeDateRange,
  onChangeBudget: useChangeBudget,
  onChangeHomeType: useChangeHomeType,
  onChangeGuests: useChangeGuests,
  otherPopularDestinationsLabel: {},
  otherPopularDestinationsValue: {},
  prePopulation: {},
  showCityCode: true,
  showDisclaimer: true,
  showFilters: true,
  showSkeletonScreen: true,
  showLoading: true,
  showTitle: true,
  skeletonSettings: {
    style: { backgroundColor: 'white' }
  },
  style: {},
  airBnBIcon: {},
  translate: () => {},
  fareItemInteractionType: 'popup',
  localizationData: null,
  theme: null,
  tracking: {
    flexibleDates: false,
    passengerCount: 1,
    promoCode: 'EVERYMUNDO',
    eventType: 'listingsCarouselBackgroundImage'
  }
};
AirBnBCarouselContainer.componentName = 'AirBnBCarouselContainer';

export default withDeepMergeDefaultProps(AirBnBCarouselContainer);
